import { lazy, Suspense, useEffect, useRef } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { LayoutWrapper } from './layouts/LayoutWrapper';
import { LayoutLogin, LanguageLayout } from './layouts';
import { ProtectedRoute } from './components/protected-route';
import { AppInitializer } from './components/app-initializer/AppInitializer';
import { LoadingSpinner } from './components/loading-spinner';
import { gaEvent, gaPageView } from './analytics/ga4';
import { GA4_EVENT } from './analytics/events';
import { stripLangPrefix } from './common/utils/locale.utils';
import { useNotificationSeen } from './common/hooks';

import './common/fonts/Gilroy-Bold.ttf';
import './common/fonts/Gilroy-Heavy.ttf';
import './common/fonts/Gilroy-Light.ttf';
import './common/fonts/Gilroy-Medium.ttf';
import './common/fonts/Gilroy-Regular.ttf';

const HomePage = lazy(() =>
  import('./pages/home-page').then((module) => ({ default: module.HomePage })),
);
const OffersPage = lazy(() =>
  import('./pages/offers-page').then((module) => ({ default: module.OffersPage })),
);
const OfferViewPage = lazy(() =>
  import('./pages/offers-page').then((module) => ({ default: module.OfferViewPage })),
);
const CategoriesPage = lazy(() =>
  import('./pages/categories-page').then((module) => ({ default: module.CategoriesPage })),
);
const CategoryOffersPage = lazy(() =>
  import('./pages/categories-page').then((module) => ({ default: module.CategoryOffersPage })),
);
const LoginPage = lazy(() =>
  import('./pages/login-page').then((module) => ({ default: module.LoginPage })),
);
const OTPPage = lazy(() =>
  import('./pages/login-page').then((module) => ({ default: module.OTPPage })),
);
const RegisterPage = lazy(() =>
  import('./pages/register-page').then((module) => ({ default: module.RegisterPage })),
);
const PromocodesPage = lazy(() =>
  import('./pages/promocodes-page').then((module) => ({ default: module.PromocodesPage })),
);
const PromocodeViewPage = lazy(() =>
  import('./pages/promocodes-page').then((module) => ({ default: module.PromocodeViewPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/profile-page').then((module) => ({ default: module.ProfilePage })),
);
const UpdateProfilePage = lazy(() =>
  import('./pages/profile-page').then((module) => ({ default: module.UpdateProfilePage })),
);
const DeleteAccountPage = lazy(() =>
  import('./pages/profile-page').then((module) => ({ default: module.DeleteAccountPage })),
);
const FavoritesPage = lazy(() =>
  import('./pages/favorites-page/FavoritesPage').then((module) => ({
    default: module.FavoritesPage,
  })),
);
const ContactPage = lazy(() =>
  import('./pages/contact-page').then((module) => ({ default: module.ContactPage })),
);

const ExternalRedirect = ({ to }) => {
  useEffect(() => {
    window.location.replace(to);
  }, [to]);

  return null;
};

const AnalyticsRouteListener = () => {
  const location = useLocation();
  const didAppOpen = useRef(false);

  useNotificationSeen();

  useEffect(() => {
    if (didAppOpen.current) return;
    didAppOpen.current = true;
    gaEvent(GA4_EVENT.APP_OPEN);
  }, []);

  useEffect(() => {
    const cleanPath = stripLangPrefix(location.pathname);
    gaPageView({
      page_path: `${cleanPath}${location.search}`,
    });
  }, [location.pathname, location.search]);

  return null;
};

const renderAppRoutes = () => [
  <Route key="contact" path="contact" element={<ContactPage />} />,
  <Route key="protected" element={<ProtectedRoute />}>
    <Route element={<LayoutWrapper />}>
      <Route index element={<HomePage />} />
      <Route path="offers/:code" element={<OffersPage />} />
      <Route path="offers/offer/:id" element={<OfferViewPage />} />
      <Route path="categories" element={<CategoriesPage />} />
      <Route path="categories/:code/:id" element={<CategoryOffersPage />} />
      <Route path="promocodes" element={<PromocodesPage />} />
      <Route path="promocodes/:id" element={<PromocodeViewPage />} />
      <Route path="profile" element={<ProfilePage />} />
      <Route path="profile/update" element={<UpdateProfilePage />} />
      <Route path="profile/delete-account" element={<DeleteAccountPage />} />
      <Route path="saved" element={<FavoritesPage />} />
    </Route>
  </Route>,
  <Route key="login" path="login" element={<LayoutLogin />}>
    <Route index element={<LoginPage />} />
    <Route path="register" element={<RegisterPage />} />
    <Route path="otp" element={<OTPPage />} />
  </Route>,
];

const App = () => {
  return (
    <AppInitializer>
      <Suspense fallback={<LoadingSpinner />}>
        <AnalyticsRouteListener />
        <Routes>
          <Route path="/privacy-policy" element={<ExternalRedirect to="/privacy-policy.html" />} />
          <Route path="/privacy-policy/" element={<ExternalRedirect to="/privacy-policy.html" />} />
          <Route path="/:lang" element={<LanguageLayout />}>
            {renderAppRoutes()}
          </Route>
          <Route path="/" element={<LanguageLayout />}>
            {renderAppRoutes()}
          </Route>
        </Routes>
      </Suspense>
    </AppInitializer>
  );
};

const RootApp = () => <App />;

export default RootApp;
